import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Container,
  Typography,
  CircularProgress,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { AccountTree } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import ResultCount from "../components/ResultCount";
import ClearFiltersButton from "../components/ClearFiltersButton";
import StandardPagination from "../components/Pagination/StandardPagination";
import { Toast } from "../components/Toast";
import { useToast } from "../hooks/useToast";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { colors, typography } from "../theme/designSystem";
import {
  workflowCatalogService,
  workflowErrorMessage,
  WorkflowCatalogItem,
  TARGET_KIND_LABELS,
} from "../services/workflowsService";

const SEARCH_DEBOUNCE_MS = 400;

const WorkflowCatalog: React.FC = () => {
  const { navigateTo } = useRouter();
  const { user } = useAuth();
  const { toast, showToast, hideToast } = useToast();
  const entityId = user?.entityId || "";

  const [items, setItems] = useState<WorkflowCatalogItem[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [groups, setGroups] = useState<string[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [groupName, setGroupName] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // A API pagina e filtra: digitar recarrega o servidor, então o termo espera o usuário parar.
  useEffect(() => {
    const timer = setTimeout(() => setAppliedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const load = async () => {
    if (!entityId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await workflowCatalogService.getByEntity(entityId, {
        search: appliedSearch,
        category: category === "all" ? undefined : category,
        groupName: groupName === "all" ? undefined : groupName,
        pageNumber: page,
        pageSize,
      });
      setItems(data.items);
      setCategories(data.categories);
      setGroups(data.groups);
      setTotalCount(data.totalCount);
      setTotalPages(data.totalPages);
    } catch (e) {
      showToast(workflowErrorMessage(e, "Erro ao carregar os fluxos de trabalho."), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Clinic4Us - Fluxos de trabalho";
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, appliedSearch, category, groupName, page, pageSize]);

  // Todo filtro volta para a primeira página: manter a página atual mostraria "nenhum resultado"
  // num conjunto que encolheu.
  const changeSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const changeCategory = (value: string) => {
    setCategory(value);
    setPage(1);
  };

  const changePageSize = (value: number) => {
    setPageSize(value);
    setPage(1);
  };

  const clearFilters = () => {
    setSearch("");
    setAppliedSearch("");
    setCategory("all");
    setGroupName("all");
    setPage(1);
  };

  const hasFilters = search.trim() !== "" || category !== "all" || groupName !== "all";

  const openWorkflow = (workflowId: string) => navigateTo("workflow-view", { workflowId });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box
        component="main"
        className="c4u-main"
      >
        <Container maxWidth={false} disableGutters>
          <Box
            sx={{
              display: "flex",
              // Ações ficam na linha do título, inclusive no celular (RN-539h): com "wrap"
              // o bloco do título quebrava antes de encolher e as ações caíam para baixo.
              flexWrap: "nowrap",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 1,
              gap: 2,
            }}
          >
            <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
              <Typography variant="h4" className="c4u-page-header__title">
                Fluxos de trabalho
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Como a clínica faz cada processo, na versão que está valendo. Abra um fluxo para ver o
                passo a passo, o vídeo de apresentação e os documentos de apoio.
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexShrink: 0,
                justifyContent: "flex-end",
              }}
            >
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">
            <div className="c4u-filters__row">
              <div className="c4u-filters__fields">
              <TextField
                label="Buscar"
                value={search}
                onChange={(e) => changeSearch(e.target.value)}
                size="small"
                sx={{ flex: "1 1 240px", minWidth: 0 }}
                inputProps={{ maxLength: 120 }}
                InputLabelProps={{ shrink: true }}
                placeholder="nome ou finalidade do processo"
              />
              <FormControl size="small" sx={{ flex: "1 1 180px", minWidth: 140 }}>
                <InputLabel shrink>Área</InputLabel>
                <Select
                  label="Área"
                  value={categories.includes(category) || category === "all" ? category : "all"}
                  onChange={(e) => changeCategory(e.target.value)}
                  notched
                  displayEmpty
                >
                  <MenuItem value="all">Todas</MenuItem>
                  {categories.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ flex: "1 1 200px", minWidth: 140 }}>
                <InputLabel shrink>Grupo</InputLabel>
                <Select
                  label="Grupo"
                  value={groups.includes(groupName) || groupName === "all" ? groupName : "all"}
                  onChange={(e) => {
                    setGroupName(e.target.value);
                    setPage(1);
                  }}
                  notched
                  displayEmpty
                >
                  <MenuItem value="all">Todos</MenuItem>
                  {groups.map((g) => (
                    <MenuItem key={g} value={g}>
                      {g}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              </div>

              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilters} />
              </div>
            </div>

            <ResultCount shown={items.length} />

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress sx={{ color: colors.primary }} />
            </Box>
          ) : items.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6, color: colors.textSecondary }}>
              <AccountTree sx={{ fontSize: 48, color: "#ced4da", mb: 1 }} />
              <Typography variant="body1" sx={{ mb: 0.5, color: colors.textPrimary }}>
                {hasFilters ? "Nenhum fluxo no filtro selecionado" : "Nenhum fluxo publicado para você"}
              </Typography>
              <Typography variant="body2">
                {hasFilters
                  ? "Ajuste a busca ou a área para ver os processos disponíveis."
                  : "Aqui aparecem os processos publicados que o seu perfil pode consultar."}
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {items.map((item, index) => (
                  <React.Fragment key={item.id}>
                    {(item.groupName || "") !== (items[index - 1]?.groupName || "") && (
                      <Typography
                        variant="caption"
                        sx={{
                          mt: index === 0 ? 0 : 1,
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          fontWeight: 700,
                          color: colors.textMuted,
                        }}
                      >
                        {item.groupName || "Sem grupo"}
                      </Typography>
                    )}
                  <Paper
                    elevation={0}
                    role="button"
                    tabIndex={0}
                    onClick={() => openWorkflow(item.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openWorkflow(item.id);
                      }
                    }}
                    sx={{
                      p: 1.5,
                      border: "1px solid #e9ecef",
                      borderRadius: "8px",
                      cursor: "pointer",
                      "&:focus-visible": { outline: `2px solid ${colors.primary}`, outlineOffset: 2 },
                      transition: "border-color 0.2s, box-shadow 0.2s",
                      "&:hover": {
                        borderColor: colors.primary,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: colors.textPrimary,
                        wordBreak: "break-word",
                        lineHeight: 1.25,
                      }}
                    >
                      {item.name}
                    </Typography>
                    {item.purpose && (
                      <Typography
                        variant="caption"
                        sx={{ display: "block", color: colors.textSecondary, wordBreak: "break-word" }}
                      >
                        {item.purpose}
                      </Typography>
                    )}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.75 }}>
                      {item.category && (
                        <Chip
                          label={item.category}
                          size="small"
                          variant="outlined"
                          sx={{ height: 22, fontSize: "0.7rem" }}
                        />
                      )}
                      <Chip
                        label={TARGET_KIND_LABELS[item.targetKind] ?? item.targetKind}
                        size="small"
                        variant="outlined"
                        sx={{ height: 22, fontSize: "0.7rem" }}
                      />
                      <Chip
                        label={`v${item.versionNumber}`}
                        size="small"
                        sx={{ height: 22, fontSize: "0.7rem", color: "#fff", bgcolor: colors.primary }}
                      />
                      <Chip
                        label={`${item.stepCount} ${item.stepCount === 1 ? "etapa" : "etapas"}`}
                        size="small"
                        variant="outlined"
                        sx={{ height: 22, fontSize: "0.7rem" }}
                      />
                    </Box>
                  </Paper>
                  </React.Fragment>
                ))}
              </Box>

              <Box sx={{ mt: 1 }}>
                <StandardPagination
                  currentPage={page}
                  totalPages={totalPages}
                  itemsPerPage={pageSize}
                  totalItems={totalCount}
                  onPageChange={setPage}
                  onItemsPerPageChange={changePageSize}
                />
              </Box>
            </>
          )}
          </div>
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />
      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </Box>
  );
};

export default WorkflowCatalog;
